// Browser-side error reporting → POST /client-error → owner alert email +
// triage-agent incident (see node/routes/clientError.js).
//
// Two halves:
//  * Breadcrumbs — a small ring buffer of what the user was doing (clicks,
//    API calls with request bodies, route changes) that rides along with
//    every report so triage can reconstruct the path to the crash.
//  * reportClientError — called from ErrorBoundary (render crashes) and the
//    global error/unhandledrejection listeners (everything else). Capped per
//    session and deduped so a crash loop can't hammer the endpoint.

import { apiFetch } from './api.js';
import { getToken } from './csrf.js';

const MAX_BREADCRUMBS = 25;
const MAX_REPORTS_PER_SESSION = 5;
const breadcrumbs = [];
const reportedKeys = new Set();
let reportsSent = 0;

const stamp = () => new Date().toISOString().slice(11, 19); // HH:MM:SS

function addBreadcrumb(type, data) {
  breadcrumbs.push({ t: stamp(), type, ...data });
  if (breadcrumbs.length > MAX_BREADCRUMBS) breadcrumbs.shift();
}

// Request bodies are included so triage can see the data in play — except on
// credential-bearing endpoints, which must never leave the browser again.
// /passkey carries an assertion, which is credential-bearing by that standard
// even though it is single-use and already spent by the time it is logged.
const SENSITIVE_PATH = /login|password|reset|newdealership|passkey/i;

/** Called from the window.fetch patch in main.jsx for every API call. */
export function apiBreadcrumb(url, options, status, ms) {
  try {
    const path = String(url).replace(/^https?:\/\/[^/]+/, '').slice(0, 200);
    if (path.startsWith('/client-error')) return; // don't narrate ourselves
    const crumb = { m: options?.method || 'GET', path, status, ms };
    if (!SENSITIVE_PATH.test(path) && typeof options?.body === 'string') {
      crumb.body = options.body.slice(0, 600);
    }
    addBreadcrumb('api', crumb);
  } catch { /* breadcrumbs must never break fetch */ }
}

function clickBreadcrumb(e) {
  try {
    const el = e.target?.closest?.('button, a, [role="button"], [role="tab"], [role="menuitem"], input[type="submit"]');
    if (!el) return;
    const text = (el.getAttribute('aria-label') || el.textContent || '').trim().slice(0, 60);
    addBreadcrumb('click', { el: el.tagName.toLowerCase(), text });
  } catch { /* ignore */ }
}

/**
 * Send an error report. Safe to call from anywhere; never throws. Skipped
 * when logged out (the endpoint sits behind the session filter), after the
 * per-session cap, or for a message we already reported this session.
 *
 * `signedOut: true` posts to the twin endpoint mounted ABOVE the session filter
 * instead, and is only for failures that happen before anyone is logged in —
 * passkey sign-in being the one that matters, since the browser is the sole
 * place its reason exists. The caps and the dedup still apply; what it skips is
 * the getToken() guard, which would otherwise drop every such report silently.
 * The server accepts a fixed list of `kind`s there, so a new signed-out caller
 * needs a matching entry in node/routes/clientError.js.
 */
export function reportClientError(error, { kind = 'window-error', label, componentStack, signedOut = false } = {}) {
  try {
    const message = String(error?.message || error || 'Unknown error').slice(0, 300);
    if (!signedOut && !getToken()) return;
    if (reportsSent >= MAX_REPORTS_PER_SESSION) return;
    if (reportedKeys.has(message)) return;
    reportedKeys.add(message);
    reportsSent++;

    apiFetch(signedOut ? '/client-error/signed-out' : '/client-error', {
      body: {
        message,
        stack: String(error?.stack || '').slice(0, 8000),
        kind,
        label,
        componentStack: componentStack ? String(componentStack).slice(0, 2000) : undefined,
        route: location.pathname + location.search + location.hash,
        breadcrumbs: breadcrumbs.slice(),
        bundle: document.querySelector('script[src*="/assets/index-"]')?.getAttribute('src') || null,
        userAgent: navigator.userAgent,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        time: new Date().toISOString(),
      },
    }).catch(() => {});
  } catch { /* reporting must never cause errors of its own */ }
}

/** Install global listeners. Called once from main.jsx. */
export function initErrorReporting() {
  window.addEventListener('error', (e) => {
    // Resource-load failures (img/script tags) surface here without an error
    // object — flaky networks make those pure noise, so only report real throws.
    if (!e.error) return;
    reportClientError(e.error, { kind: 'window-error' });
  });

  window.addEventListener('unhandledrejection', (e) => {
    const reason = e.reason instanceof Error ? e.reason : new Error(String(e.reason ?? 'Unhandled rejection'));
    // Network blips are the user's connection, not a bug.
    if (/failed to fetch|networkerror|load failed/i.test(reason.message)) return;
    reportClientError(reason, { kind: 'unhandledrejection' });
  });

  document.addEventListener('click', clickBreadcrumb, { capture: true, passive: true });
  window.addEventListener('popstate', () => addBreadcrumb('route', { to: location.pathname + location.hash }));
  addBreadcrumb('session', { route: location.pathname + location.search });
}
